import { queryOptions, skipToken } from "@tanstack/vue-query";
import { getLdapSettings, getSamlSettings, getSubInstanceInfo } from "ls/api/cloud/admin";
import { enabledForOrganization } from "ls/api/cloud/disko";
import { getAccounts, getCloudPlusStatus, getFilesystemMappings, getOrganization, getOrganizations, getOrganizationSubscriptions, getSeatsAndLicensesInfo, getSubscriptionPurchaseInfo, getUserOrganizationsSubscriptionsInfo } from "ls/api/cloud/organization";
import { STALE } from "ls/queries/staleTime";

export const queries = {
  organizations: () => ["organizations"] as const,
  organization: (id: string) => [...queries.organizations(), id] as const,

  list: () => queryOptions({
    queryKey: [...queries.organizations(), "list"] as const,
    queryFn: ({ signal }) => getOrganizations({ signal }),
  }),

  info: (id: string | undefined) =>
    queryOptions({
      queryKey: [...queries.organization(id ?? ""), "info"] as const,
      queryFn: id ? ({ signal }) => getOrganization({ organizationId: id }, { signal }) : skipToken,
    }),

  accounts: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "accounts"] as const,
      queryFn: async ({ signal }) => {
        const response = await getAccounts({ organizationId: id }, { signal });
        return response.Items;
      },
    }),

  filesystemMappings: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "fs-mappings"] as const,
      queryFn: ({ signal }) => getFilesystemMappings({ organizationId: id }, { signal }),
    }),

  adminInfo: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "admin-info"] as const,
      queryFn: ({ signal }) => getSubInstanceInfo({ organizationId: id }, { signal }),
    }),

  samlSettings: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "saml-settings"] as const,
      queryFn: ({ signal }) => getSamlSettings({ signal }),
    }),

  ldapSettings: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "ldap-settings"] as const,
      queryFn: ({ signal }) => getLdapSettings({ signal }),
    }),
  diskoEnabled: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "disko-enabled"] as const,
      queryFn: ({ signal }) => enabledForOrganization({ organizationId: id }, { signal }),
      staleTime: STALE.INFINITY,
    }),

  organizationsSubscriptionsInfo: () =>
    queryOptions({
      queryKey: [...queries.organizations(), "organizations-subscriptions-info"] as const,
      queryFn: ({ signal }) => getUserOrganizationsSubscriptionsInfo({ signal }),
    }),
  subscriptionsInfo: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "subscriptions-info"] as const,
      queryFn: ({ signal }) => getOrganizationSubscriptions({ organizationId: id }, { signal }),
    }),
  seatsAndLicensesInfo: (id: string) =>
    queryOptions({
      queryKey: [...queries.organization(id), "cloud-author-seat-info"] as const,
      queryFn: ({ signal }) =>
        getSeatsAndLicensesInfo({ organizationId: id }, { signal }),
    }),
  purchaseInfo: (organizationId: string, subscriptionId: string) =>
    queryOptions({
      queryKey: [...queries.organization(organizationId), "purchase-info", subscriptionId] as const,
      queryFn: ({ signal }) => getSubscriptionPurchaseInfo({ organizationId, subscriptionId }, { signal }),
    }),
  cloudPlusStatus: (organizationId: string) =>
    queryOptions({
      queryKey: [...queries.organization(organizationId), "cloud-plus-status"] as const,
      queryFn: ({ signal }) => getCloudPlusStatus({ organizationId }, { signal }),
    }),
};
